import React from 'react';
import { CaseStudyShell } from '../components/case-study/CaseStudyShell';
import { CaseHeader } from '../components/case-study/CaseHeader';
import { CaseSection } from '../components/case-study/CaseSection';
import { Figure } from '../components/case-study/Figure';
import { NextCase } from '../components/case-study/NextCase';
import { Reveal } from '../components/Reveal';
import { DesignVsLiveTable } from '../components/clear-docs/DesignVsLiveTable';
import { ImageFigure } from '../components/case-study/ImageFigure';
import { clearDocsImages } from '../data/clearDocsImages';
import { clearDocsMeta, designVsLiveCalls, desktopOnly, mobileScope, reviewCleanedUp, reviewDecided, reviewFindings } from '../data/clearDocs';
export function ClearDocsCaseStudy() {
  return <CaseStudyShell data-id="element-850" data-mp="1qzygt9m6vl9b">
      <CaseHeader overline="Case study · Mortgage lending · Mobile" title="Clear Docs Mobile: deciding what loan officers don't need on a phone" intro="Clear Docs is the desktop tool loan officers use to collect and review borrower documents. The ask was a mobile version, and the requirements were incomplete. I defined the flows step by step with the product's stakeholder and kept the scope small on purpose: accept, reject or archive a document, and tag it. That work then reshaped how review works on desktop too." meta={clearDocsMeta} data-id="element-851" data-mp="1r0glmumx37su" />

      <ImageFigure className="mt-14" {...clearDocsImages.mobileScreens} caption="The mobile app: the document list, categorizing a document, requesting documents and setting auto reminders." data-id="element-852" data-mp="2gdxl374ghpdo" />

      <CaseSection index="01" label="The problem" title="A desktop tool doesn't shrink" intro={<>
            <p data-id="element-854" data-mp="vxkm1qldd02m">
              Desktop Clear Docs is dense: requests, draft and final documents, split, merge, page sorting, drop zones,
              bulk actions and previews that only appear on hover.
            </p>
            <p data-id="element-855" data-mp="sf50yru7wi6z">
              The mobile requirements didn't say which of that mattered. Rebuilding all of it on a phone would have been
              slow to ship and hard to use, so the real design question was what to leave out.
            </p>
          </>} data-id="element-853" data-mp="27belqwzvpuw6">
        <ImageFigure {...clearDocsImages.desktopAudit} dark caption="My audit of the live desktop flows, the reference for every mobile decision." data-id="element-856" data-mp="jhb9bu7eecq5" />
      </CaseSection>

      <CaseSection index="02" label="Design vs. live" title="I designed from what desktop actually does" data-id="element-857" data-mp="efvl5t9xbq3j">
        <p className="max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-858" data-mp="3vwn970dwjr7x">
          The original desktop design and the live product had drifted apart. Designing mobile from the old design file
          would have promised features that don't exist. So I compared the two, area by area, and decided what mobile
          should do in each one.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-859" data-mp="3rtyw2gi5ny55">
          {designVsLiveCalls.map((c, i) => <Reveal key={c.title} delay={i * 0.04} className="h-full" data-id="element-860" data-mp="38at9zowcrens" data-mpi={i}>
              <div className="h-full rounded-2xl bg-surface p-5" data-id="element-861" data-mp="33bghoomppfdt">
                <h3 className="text-sm font-semibold text-fg" data-id="element-862" data-mp="3h9wu8w79a03v">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg-muted" data-id="element-863" data-mp="3d8b9ypse6jjn">{c.body}</p>
              </div>
            </Reveal>)}
        </div>

        <Figure className="mt-10" caption="The full comparison: 16 areas, each with a call for mobile." data-id="element-864" data-mp="3r69p3xejek07">
          <DesignVsLiveTable data-id="element-865" data-mp="3m8hli64j59g6" />
        </Figure>
      </CaseSection>

      <CaseSection index="03" label="Scope" title="A deliberately small mobile experience" data-id="element-866" data-mp="4r84qcd3lc8m">
        <p className="max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-867" data-mp="3vyaxh88l3qky">
          With requirements this incomplete, I worked the flows out iteratively with the product's stakeholder instead
          of waiting for a full spec. A PRD-style document captured the core flows, the target users, and every change
          from desktop, so the scope was written down and agreed.
        </p>

        <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-868" data-mp="288mxbeu1qy2i">
          <Reveal className="h-full" data-id="element-869" data-mp="28722rp7qff4n">
            <div className="h-full rounded-2xl bg-ink-soft p-6" data-id="element-870" data-mp="2czd2aqyk8ywo">
              <h3 className="text-sm font-semibold text-ink" data-id="element-871" data-mp="2d28yoaurffj0">On mobile</h3>
              <ul className="mt-3 space-y-2 text-sm text-fg" data-id="element-872" data-mp="2h0osoyp9do5x">
                {mobileScope.map(item => <li key={item} className="flex gap-2.5" data-id="element-873" data-mp="1kdael872u3b2">
                    <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-ink" data-id="element-874" data-mp="1pbbwhpsrnwky" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </Reveal>
          <Reveal className="h-full" delay={0.05} data-id="element-875" data-mp="1ybs0d0df95qc">
            <div className="h-full rounded-2xl bg-surface p-6" data-id="element-876" data-mp="34rb0covcp6fe">
              <h3 className="text-sm font-semibold text-fg" data-id="element-877" data-mp="34qsxh9e6ntyi">Left on desktop or simplified</h3>
              <ul className="mt-3 space-y-2 text-sm text-fg-muted" data-id="element-878" data-mp="39oavi4qnworq">
                {desktopOnly.map(item => <li key={item} className="flex gap-2.5" data-id="element-879" data-mp="3jw6tk9t43v16">
                    <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-fg-faint" data-id="element-880" data-mp="3ewam20l7hlgv" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </Reveal>
        </div>

        <ImageFigure className="mt-10" {...clearDocsImages.coreFlows} padded caption="The three core mobile flows: request, review and upload. The request flow (outlined) got the most detail, since it's where a loan officer acts on the go." data-id="element-881" data-mp="360l461iyv988" />
      </CaseSection>

      <CaseSection index="04" label="The design system" title="Adapting a library that wasn't ready for mobile" intro={<>
            <p data-id="element-883" data-mp="pdie1gjn0s8i">
              High fidelity started in Figma and moved to Claude Design and Claude Code, built on the Clear component
              library, so the prototype uses the same components engineering does.
            </p>
            <p data-id="element-884" data-mp="2jlvwts6reuhi">
              The library wasn't fully mobile-ready. The team was still fixing accuracy issues between design and code,
              and there was no mobile tabs component. The prototype uses a two-tab pattern (Draft documents and Final in
              LOS on the list, Preview and Properties in the viewer) while the team decided how tabs should work on
              mobile.
            </p>
          </>} data-id="element-882" data-mp="2r784stuhhumw" />

      <CaseSection index="05" label="Document review" title="One review model: the button you press sets the status" intro={<>
            <p data-id="element-886" data-mp="2tjtlggsj6tan">
              Mobile's Accept and Reject buttons raised a bigger question: what does reviewing a document actually do? On
              desktop the answer was messy.
            </p>
            <p data-id="element-887" data-mp="1i4n4hdrqrmuy">
              In mid-July I led the team's alignment sessions and consolidated the decisions around one goal: Clear Docs
              and the Conditions Portal share one document-properties component and one role-aware button and status
              model. Every change was framed as streamlining the workflow, with nothing taken away.
            </p>
          </>} data-id="element-885" data-mp="32jp0iwp7j8fy">
        <div className="grid gap-4 md:grid-cols-3" data-id="element-888" data-mp="1en8tag5vdlfg">
          {reviewFindings.map((f, i) => <Reveal key={f.title} delay={i * 0.04} className="h-full" data-id="element-889" data-mp="qx50z4y4t83r" data-mpi={i}>
              <div className={`h-full rounded-2xl p-5 ${f.highlight ? 'bg-coral-soft' : 'bg-surface'}`} data-id="element-890" data-mp="qvbv3rvcerwt">
                <h3 className={`text-sm font-semibold ${f.highlight ? 'text-rust' : 'text-fg'}`} data-id="element-891" data-mp="lwgynn4gd4u8">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg-muted" data-id="element-892" data-mp="vwhozt0qol7x">{f.body}</p>
              </div>
            </Reveal>)}
        </div>

        <Reveal className="mt-4" data-id="element-893" data-mp="85lxkx073s17">
          <div className="rounded-2xl bg-ink p-6 text-white md:p-8" data-id="element-894" data-mp="84katv9thh3n">
            <p className="text-xs font-medium text-white/70" data-id="element-895" data-mp="37ao4ndtbjji">The behavioral call</p>
            <p className="mt-2 max-w-3xl text-lg font-semibold leading-snug md:text-xl" data-id="element-896" data-mp="3duy9s0671xsf">
              When the wrong option is one slip away, people pick it. So nobody picks a status anymore. The button sets
              it: Accept, Approve or Ready to Ship, depending on your role. No misclicks, and nothing to think about.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-10 md:grid-cols-2 md:gap-12" data-id="element-897" data-mp="nwnr0dnll3h">
          {[{
          title: 'What we decided',
          items: reviewDecided
        }, {
          title: 'What we cleaned up',
          items: reviewCleanedUp
        }].map(list => <div key={list.title} data-id="element-898" data-mp="ady9vmmg4tfr">
              <h3 className="text-sm font-semibold text-ink" data-id="element-899" data-mp="fc9gzplcwpua">{list.title}</h3>
              <ul className="mt-3 border-t border-line" data-id="element-900" data-mp="fecbaltjl54t">
                {list.items.map(item => <li key={item} className="border-b border-line py-3 text-sm leading-relaxed text-fg-muted" data-id="element-901" data-mp="k9j4v0t3ktg0">
                    {item}
                  </li>)}
              </ul>
            </div>)}
        </div>

        <ImageFigure className="mt-12" {...clearDocsImages.beforeViewer} caption="Before: a status dropdown, two overlapping checkboxes, and Send to LOS as its own step." data-id="element-902" data-mp="24t533pdhndo4" />

        <div className="mt-10 grid gap-6 md:grid-cols-2" data-id="element-903" data-mp="1uw8s9d04afge">
          <ImageFigure {...clearDocsImages.acceptMoves} caption="Accepting moves a document out of Needs Review and into its category." data-id="element-904" data-mp="2sizdm0ac4q57" />
          <ImageFigure {...clearDocsImages.acceptLands} caption="There, its status reads Reviewed, shown read-only in the list." data-id="element-905" data-mp="2nko2lm8b09le" />
        </div>

        <ImageFigure className="mt-10" {...clearDocsImages.funnel} caption="The button and status funnel by permission, draft side." data-id="element-906" data-mp="2dul5b9u5g9h9" />

        <ImageFigure className="mt-10" {...clearDocsImages.buttonRules} caption="Which primary button each role sees, and what it does." data-id="element-907" data-mp="36iqovt9gvsfi" />

        <ImageFigure className="mt-10" {...clearDocsImages.decisionsBoard} caption="The decisions board, kept separate on purpose: decided, findings, proposals, open questions and parked items." data-id="element-908" data-mp="1rdo9k8ndsu0v" />
      </CaseSection>

      <CaseSection index="06" label="Where it stands" title="Work in progress" intro={<>
            <p data-id="element-910" data-mp="2aah6qfzo6f0h">
              By late June 2026, the mobile flows were defined and documented, and the high-fidelity prototype was in
              progress. By mid-July, the review button and status model was decided, with a few questions still open,
              like how splitting a condition-linked document should behave.
            </p>
            <p data-id="element-911" data-mp="2t2i9htfkepf6">
              The work is now in the development queue. It hasn't launched, so there are no results to report yet. What
              this project shows is the part before launch: turning a vague ask and a messy model into decisions
              everyone agreed on, ready for engineering to build.
            </p>
          </>} data-id="element-909" data-mp="1wc9bqdm71nry" />

      <NextCase title="Kordis: an AI CFO whose numbers you can check" href="/work/kordis" data-id="element-912" data-mp="3cns8fsh8vi8y" />
    </CaseStudyShell>;
}